import React from "react";
import { Edit2, Trash2, Clock, User, BookOpen } from "lucide-react";
import { Button, Badge } from "../../components/ui";

const PERIOD_TYPE_BADGE_VARIANTS = {
  CLASS: "primary",
  BREAK: "secondary",
  ASSEMBLY: "purple",
  FREE: "success",
  LAB: "warning",
  ACTIVITY: "pink",
};

const PERIOD_TYPE_COLORS = {
  CLASS: "bg-blue-50/50 border-blue-200 text-blue-950",
  BREAK: "bg-neutral-50/80 border-neutral-200 dark:border-neutral-800 text-neutral-700 dark:text-neutral-200",
  ASSEMBLY: "bg-purple-50/50 border-purple-200 text-purple-950",
  FREE: "bg-emerald-50/50 border-emerald-200 text-emerald-950",
  LAB: "bg-amber-50/50 border-amber-200 text-amber-950",
  ACTIVITY: "bg-pink-50/50 border-pink-200 text-pink-950",
};

export default function PeriodCard({ period, canManage, onEdit, onDelete }) {
  const isNonSubject = ["BREAK", "ASSEMBLY", "FREE"].includes(period.periodType);
  const cardColorClass =
    PERIOD_TYPE_COLORS[period.periodType] || "bg-neutral-50 dark:bg-neutral-950 border-neutral-200 dark:border-neutral-800 text-neutral-800 dark:text-neutral-100";
  const badgeVariant = PERIOD_TYPE_BADGE_VARIANTS[period.periodType] || "secondary";

  const subjectColor = period.subjectId?.color || "#3B82F6";

  return (
    <div
      className={`group relative flex w-full h-full min-h-0 flex-col justify-between gap-2 rounded-none border p-2.5 transition-all duration-150 hover:shadow-sm ${cardColorClass}`}
    >
      {/* Top accent line indicator for subject color */}
      {!isNonSubject && period.subjectId?.color && (
        <div
          className="absolute inset-x-0 top-0 h-1 shrink-0"
          style={{ backgroundColor: subjectColor }}
          aria-hidden="true"
        />
      )}

      <div className={`space-y-2 min-w-0 ${!isNonSubject && period.subjectId?.color ? "pt-1" : ""}`}>
        {/* Header Badges */}
        <div className="flex items-center justify-between gap-1 flex-wrap">
          <Badge
            variant="outline"
            size="sm"
            className="rounded-none bg-white/90 dark:bg-neutral-900/90 font-bold text-neutral-900 dark:text-neutral-100 border-neutral-300 dark:border-neutral-700 text-[10px] px-1.5 whitespace-nowrap"
          >
            P{period.periodNumber}
          </Badge>
          <Badge
            variant={badgeVariant}
            size="sm"
            className="rounded-none uppercase tracking-wider text-[10px] px-1.5 py-0.5 font-medium whitespace-nowrap"
          >
            {period.periodType}
          </Badge>
        </div>

        {/* Time Row */}
        <div className="flex items-center gap-1 text-[11px] font-medium opacity-80 min-w-0 flex-wrap">
          <Clock className="h-3 w-3 shrink-0" />
          <span className="break-words">
            {period.startTime} - {period.endTime}
          </span>
        </div>

        {/* Content Section */}
        {isNonSubject ? (
          <div className="py-2 text-center font-bold text-xs tracking-wider uppercase opacity-75 break-words">
            {period.periodType}
          </div>
        ) : (
          <div className="space-y-1.5 pt-0.5 min-w-0">
            {period.subjectId && (
              <div className="font-bold text-xs sm:text-sm flex items-start gap-1.5 leading-snug text-neutral-900 dark:text-neutral-100 min-w-0">
                <BookOpen className="h-3.5 w-3.5 mt-0.5 shrink-0 opacity-70" />
                <span className="break-words whitespace-normal">
                  {period.subjectId.name}{" "}
                  {period.subjectId.code && (
                    <span className="text-[10px] font-normal opacity-70 break-words">
                      ({period.subjectId.code})
                    </span>
                  )}
                </span>
              </div>
            )}

            {period.staffId && (
              <div className="text-xs font-medium flex items-start gap-1 opacity-80 text-neutral-800 dark:text-neutral-100 min-w-0">
                <User className="h-3 w-3 shrink-0 mt-0.5" />
                <span className="break-words whitespace-normal">{period.staffId.name}</span>
              </div>
            )}
          </div>
        )}
      </div>

      {/* Action Buttons */}
      {canManage && (
        <div className="mt-2 flex items-center justify-end gap-1 border-t border-black/10 pt-1.5 opacity-100 sm:opacity-0 sm:group-hover:opacity-100 transition-opacity shrink-0">
          <Button
            type="button"
            variant="ghost"
            size="xs"
            onClick={() => onEdit(period)}
            title="Edit Period"
            className="h-6 w-6 p-0 rounded-none hover:bg-white/80 shrink-0"
          >
            <Edit2 className="h-3.5 w-3.5 text-neutral-700 dark:text-neutral-200" />
          </Button>
          <Button
            type="button"
            variant="ghost"
            size="xs"
            onClick={() => onDelete(period._id)}
            title="Delete Period"
            className="h-6 w-6 p-0 rounded-none text-danger-600 hover:bg-white/80 hover:text-danger-700 shrink-0"
          >
            <Trash2 className="h-3.5 w-3.5" />
          </Button>
        </div>
      )}
    </div>
  );
}